import { useApp } from "@/store/app";
import Xmpp from "../Xmpp";

export default function createNetworkManager() {
  let xmpp: Xmpp | null = null;
  let timeoutId: NodeJS.Timeout | null = null;
  const setStatus = useApp.getState().setStatus;

  const reconnect = async () => {
    log.app("Reconnecting...");
    try {
      await xmpp?.reconnect();
      setStatus("STARTED");
    } catch (err) {
      log.app("Error while restarting xmpp", err);
      timeoutId = setTimeout(() => reconnect(), 5000);
    }
  };

  const isAppReady = () => {
    const status = useApp.getState().status;
    const isReady = status !== "STARTING" && status !== "FAILURE" && status !== "ALREADY_RUNNING";
    if (!isReady) log.app("App has not started, ignoring network event");
    return isReady;
  };

  const onOnline = async () => {
    log.app("Network available");
    if (!isAppReady()) return;
    if (timeoutId) clearTimeout(timeoutId);
    try {
      await xmpp?.connect();
      await xmpp?.getRoster();
      xmpp?.pingService.start();
      xmpp?.sendPresence();
    } catch (err) {
      log.app("Network available, but error connecting to xmpp", err);
      reconnect();
      return;
    }
    setStatus("STARTED");
  };

  const onOffline = () => {
    log.app("Network unavailable");
    if (!isAppReady()) return;
    setStatus("NO_NETWORK");
  };

  const handleStart = async (xmppCredentials: XmppCredentials) => {
    try {
      xmpp = Xmpp.getInstance();
      xmpp.setCredentials(xmppCredentials);
      await xmpp.connect();
      xmpp.pingService.start();
      xmpp.sendPresence("not_chosen_yet");
    } catch (err) {
      log.app("Error while starting app", err);
      try {
        await xmpp?.disconnect();
      } catch (err) {
        log.app("Couldn't disconnect from xmpp on app failure", err);
      }
      throw Error("NetworkManager 'handleStart' failed", { cause: err });
    }
  };

  function startListening() {
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
  }

  function stopListening() {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
  }

  return {
    start: handleStart,
    reconnect,
    startListening,
    stopListening
  };
}
